export const TRACKING_SCHEMA_VERSION = 1;

export const ASSET_CATEGORIES = [
  'Line Medic Pack', 'Trauma Bag', 'O2 Bag', 'AED', 'Radio Antenna', 'Clipboard', 'Ice Chest', 'Combi Tool', 'Radio Set', 'Line Medic Set', 'Personal Set', 'Other',
];

export const DEPLOYMENT_TYPES = ['EMPF', 'EMTF', 'REMS'];

export const RETURN_CONDITIONS = [
  { value: 'ready', label: 'Ready for service' },
  { value: 'needs_restock', label: 'Needs restock' },
  { value: 'damaged', label: 'Damaged / needs attention' },
];

export const STANDARD_SET_COMPONENTS = [
  { id: 'line-medic-pack', label: 'Mystery Ranch Line Medic Pack' },
  { id: 'red-trauma-bag', label: 'Red Trauma Bag' },
  { id: 'o2-bag', label: 'Green O2 Bag' },
  { id: 'zoll-aed', label: 'Zoll AED' },
  { id: 'radio-antenna', label: 'Radio Antenna' },
  { id: 'clipboard-pcr-ama', label: "Clipboard with AMA's and PCR's" },
];

export const GREG_SET_COMPONENTS = STANDARD_SET_COMPONENTS
  .filter(component => component.id !== 'zoll-aed')
  .map(component => component.id === 'o2-bag' ? { ...component, label: 'Orange O2 Bag' } : component);

function nowIso() { return new Date().toISOString(); }
function makeId(prefix) { return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8).toUpperCase()}`; }

export function normalizeAssetId(value) {
  const raw = String(value || '').trim();
  if (!raw) return '';
  try {
    const url = new URL(raw);
    const match = url.pathname.match(/\/gear\/([^/?#]+)/i);
    if (match) return decodeURIComponent(match[1]).trim().toUpperCase();
  } catch (_) {}
  const gearMatch = raw.match(/(?:^|\/)gear\/([^/?#]+)/i);
  return decodeURIComponent(gearMatch ? gearMatch[1] : raw).trim().toUpperCase();
}

export function getAssetComponents(asset) {
  const id = normalizeAssetId(asset?.id);
  if (id === 'GREG-GEAR') return GREG_SET_COMPONENTS;
  if (asset?.category === 'Line Medic Set' || /^LM-SET-\d+$/i.test(id)) return STANDARD_SET_COMPONENTS;
  return [];
}

function normalizeComponentSelections(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  const result = {};
  Object.entries(value).forEach(([assetId, componentIds]) => {
    const normalizedAssetId = normalizeAssetId(assetId);
    if (!normalizedAssetId || !Array.isArray(componentIds)) return;
    result[normalizedAssetId] = [...new Set(componentIds.map(item => String(item || '').trim()).filter(Boolean))];
  });
  return result;
}

export function getDeploymentComponentIds(deployment, assetId) {
  const id = normalizeAssetId(assetId);
  const selections = normalizeComponentSelections(deployment?.componentSelections);
  return Array.isArray(selections[id]) ? selections[id] : [];
}

export function getDeploymentComponentLabels(deployment, asset) {
  const selected = getDeploymentComponentIds(deployment, asset?.id);
  if (!selected.length) return [];
  const components = getAssetComponents(asset);
  return selected.map(id => components.find(component => component.id === id)?.label || id);
}

export function createInitialTrackingState() {
  const createdAt = nowIso();
  return { schemaVersion: TRACKING_SCHEMA_VERSION, assets: [{ id: 'GREG-GEAR', name: "Greg's Gear", category: 'Personal Set', ownership: 'personal', personalOwner: 'Greg', status: 'available', condition: 'ready', currentDeploymentId: null, notes: 'Legacy personal deployment bundle. Keep active until individual personal assets replace it.', active: true, createdAt, updatedAt: createdAt }], deployments: [], events: [], recentIncidents: [] };
}

export function hydrateTrackingState(raw) {
  const base = createInitialTrackingState();
  if (!raw || typeof raw !== 'object') return base;
  const assets = Array.isArray(raw.assets) ? raw.assets.filter(Boolean) : [];
  const hasGregGear = assets.some(asset => normalizeAssetId(asset?.id) === 'GREG-GEAR');
  const deployments = Array.isArray(raw.deployments) ? raw.deployments.filter(Boolean).map(item => ({
    ...item,
    deploymentType: String(item?.deploymentType || item?.role || 'EMPF').trim() || 'EMPF',
    incidentOrderNumber: String(item?.incidentOrderNumber || '').trim().toUpperCase(),
    incidentIrwinId: String(item?.incidentIrwinId || '').trim(),
    incidentSource: String(item?.incidentSource || '').trim() || 'Manual',
    deploymentSource: String(item?.deploymentSource || '').trim(),
    deploymentSourceId: String(item?.deploymentSourceId || '').trim(),
    deploymentSourcePosition: String(item?.deploymentSourcePosition || '').trim(),
    deploymentSourceLocation: String(item?.deploymentSourceLocation || '').trim(),
    componentSelections: normalizeComponentSelections(item?.componentSelections),
  })) : [];
  return { schemaVersion: TRACKING_SCHEMA_VERSION, assets: hasGregGear ? assets : [base.assets[0], ...assets], deployments, events: Array.isArray(raw.events) ? raw.events.filter(Boolean) : [], recentIncidents: Array.isArray(raw.recentIncidents) ? raw.recentIncidents.filter(Boolean).slice(0, 20) : [] };
}

function event(type, actor, details = {}) { return { id: makeId('EVT'), type, actor: String(actor || 'Unknown').trim() || 'Unknown', timestamp: nowIso(), ...details }; }
function assetReadyForCheckout(asset) { return asset && asset.active !== false && asset.status === 'available' && asset.condition === 'ready'; }
function personTokens(value) { return String(value || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim().split(/\s+/).filter(Boolean); }

export function personalOwnerMatchesPerson(owner, person) {
  const ownerTokens = personTokens(owner), personNameTokens = personTokens(person);
  if (!ownerTokens.length || !personNameTokens.length) return false;
  const ownerKey = ownerTokens.join(' '), personKey = personNameTokens.join(' ');
  if (ownerKey === personKey) return true;
  if (ownerTokens.length === 1) return personNameTokens.includes(ownerTokens[0]);
  if (personNameTokens.length === 1) return ownerTokens.includes(personNameTokens[0]);
  return ownerTokens.every(token => personNameTokens.includes(token));
}

export function addAsset(state, input, actor = 'Greg') {
  const id = normalizeAssetId(input?.id), name = String(input?.name || '').trim();
  if (!id) throw new Error('Asset ID is required.');
  if (!name) throw new Error('Asset name is required.');
  if (state.assets.some(asset => normalizeAssetId(asset.id) === id)) throw new Error(`Asset ${id} already exists.`);
  const ownership = input?.ownership === 'personal' ? 'personal' : 'program';
  const personalOwner = ownership === 'personal' ? String(input?.personalOwner || '').trim() : '';
  if (ownership === 'personal' && !personalOwner) throw new Error('Personal owner is required for personal gear.');
  const timestamp = nowIso();
  const asset = { id, name, category: ASSET_CATEGORIES.includes(input?.category) ? input.category : 'Other', ownership, personalOwner, status: 'available', condition: 'ready', currentDeploymentId: null, notes: String(input?.notes || '').trim(), active: true, createdAt: timestamp, updatedAt: timestamp };
  return { ...state, assets: [...state.assets, asset], events: [event('asset_created', actor, { assetId: id, assetName: name }), ...state.events] };
}

export function markAssetReady(state, assetId, actor = 'Greg') {
  const id = normalizeAssetId(assetId); let found = null;
  const assets = state.assets.map(asset => {
    if (normalizeAssetId(asset.id) !== id) return asset;
    found = asset;
    if (asset.currentDeploymentId) throw new Error(`${asset.name} is still assigned to a deployment.`);
    return { ...asset, status: 'available', condition: 'ready', updatedAt: nowIso() };
  });
  if (!found) throw new Error(`Asset ${id} was not found.`);
  return { ...state, assets, events: [event('asset_ready', actor, { assetId: id, assetName: found.name }), ...state.events] };
}

export function checkoutDeployment(state, input) {
  const person = String(input?.person || '').trim();
  const incident = String(input?.incident || '').trim();
  const incidentOrderNumber = String(input?.incidentOrderNumber || '').trim().toUpperCase();
  const incidentIrwinId = String(input?.incidentIrwinId || '').trim();
  const incidentSource = String(input?.incidentSource || '').trim();
  const role = String(input?.role || '').trim();
  const deploymentType = String(input?.deploymentType || role || 'EMPF').trim() || 'EMPF';
  const deploymentSource = String(input?.deploymentSource || '').trim();
  const deploymentSourceId = String(input?.deploymentSourceId || '').trim();
  const deploymentSourcePosition = String(input?.deploymentSourcePosition || '').trim();
  const deploymentSourceLocation = String(input?.deploymentSourceLocation || '').trim();
  const startDate = String(input?.startDate || '').trim();
  const actor = String(input?.actor || person || 'Unknown').trim();
  const assetIds = [...new Set((input?.assetIds || []).map(normalizeAssetId).filter(Boolean))];
  const requestedComponentSelections = normalizeComponentSelections(input?.componentSelections);
  if (!person) throw new Error('Member name is required.');
  if (!incident) throw new Error('Incident name is required.');
  if (!startDate) throw new Error('Deployment date is required.');
  if (!assetIds.length) throw new Error('Scan or select at least one gear asset.');

  const componentSelections = {};
  const selectedAssets = assetIds.map(id => {
    const asset = state.assets.find(item => normalizeAssetId(item.id) === id);
    if (!asset) throw new Error(`Asset ${id} was not found.`);
    if (!assetReadyForCheckout(asset)) throw new Error(`${asset.name} is not ready for deployment.`);
    if (asset.ownership === 'personal' && asset.personalOwner && !personalOwnerMatchesPerson(asset.personalOwner, person)) throw new Error(`${asset.name} is personal gear assigned to ${asset.personalOwner}.`);
    const components = getAssetComponents(asset);
    if (components.length) {
      const allowed = new Set(components.map(component => component.id));
      const selected = (requestedComponentSelections[id] || []).filter(componentId => allowed.has(componentId));
      if (!selected.length) throw new Error(`Select at least one item being taken from ${asset.name}.`);
      componentSelections[id] = [...new Set(selected)];
    }
    return asset;
  });

  const deploymentId = makeId('DEP'), timestamp = nowIso();
  const deployment = { id: deploymentId, person, incident, incidentOrderNumber, incidentIrwinId, incidentSource, role, deploymentType, deploymentSource, deploymentSourceId, deploymentSourcePosition, deploymentSourceLocation, componentSelections, startDate, status: 'active', loanerRadio: String(input?.loanerRadio || '').trim(), loanerRadioReturned: false, assetIds, createdAt: timestamp, updatedAt: timestamp, returnedAt: null };
  const assets = state.assets.map(asset => assetIds.includes(normalizeAssetId(asset.id)) ? { ...asset, status: 'deployed', currentDeploymentId: deploymentId, updatedAt: timestamp } : asset);
  const sourceDetails = { deploymentSource, deploymentSourceId, deploymentSourcePosition, deploymentSourceLocation };
  const incidentDetails = { incident, incidentOrderNumber, incidentIrwinId, incidentSource };
  const events = [
    event('deployment_checkout', actor, { deploymentId, person, ...incidentDetails, ...sourceDetails, role, deploymentType, componentSelections, assetIds, loanerRadio: deployment.loanerRadio }),
    ...selectedAssets.map(asset => event('asset_checkout', actor, { deploymentId, person, ...incidentDetails, ...sourceDetails, deploymentType, assetId: asset.id, assetName: asset.name, componentIds: componentSelections[normalizeAssetId(asset.id)] || [] })),
    ...state.events,
  ];
  const recentIncidents = [incident, ...state.recentIncidents.filter(item => item.toLowerCase() !== incident.toLowerCase())].slice(0, 20);
  return { ...state, assets, deployments: [deployment, ...state.deployments], events, recentIncidents };
}

export function returnAssets(state, input) {
  const returnedBy = String(input?.returnedBy || '').trim();
  const returns = Array.isArray(input?.returns) ? input.returns : [];
  const loanerReturned = input?.loanerReturnedByDeployment || {};
  if (!returnedBy) throw new Error('Returned by is required.');
  if (!returns.length) throw new Error('Scan or select at least one deployed asset.');
  const timestamp = nowIso();
  const normalizedReturns = returns.map(item => ({
    assetId: normalizeAssetId(item?.assetId),
    condition: RETURN_CONDITIONS.some(option => option.value === item?.condition) ? item.condition : 'ready',
    handoffSessionId: String(item?.handoffSessionId || '').trim(),
    handoffType: String(item?.handoffType || '').trim(),
    returnedOnBehalfOf: String(item?.returnedOnBehalfOf || '').trim(),
    returnedOnBehalfOfUserId: String(item?.returnedOnBehalfOfUserId || '').trim(),
  }));
  const deploymentIds = new Set(), returnEvents = [];
  const assets = state.assets.map(asset => {
    const match = normalizedReturns.find(item => item.assetId === normalizeAssetId(asset.id));
    if (!match) return asset;
    if (!asset.currentDeploymentId) throw new Error(`${asset.name} is not currently deployed.`);
    const deployment = state.deployments.find(item => item.id === asset.currentDeploymentId);
    deploymentIds.add(asset.currentDeploymentId);
    const status = match.condition === 'ready' ? 'available' : match.condition;
    returnEvents.push(event('asset_return', returnedBy, {
      deploymentId: asset.currentDeploymentId,
      deployedTo: deployment?.person || '',
      incident: deployment?.incident || '',
      incidentOrderNumber: deployment?.incidentOrderNumber || '',
      incidentIrwinId: deployment?.incidentIrwinId || '',
      incidentSource: deployment?.incidentSource || '',
      deploymentType: deployment?.deploymentType || deployment?.role || 'EMPF',
      deploymentSource: deployment?.deploymentSource || '',
      deploymentSourceId: deployment?.deploymentSourceId || '',
      deploymentSourcePosition: deployment?.deploymentSourcePosition || '',
      deploymentSourceLocation: deployment?.deploymentSourceLocation || '',
      assetId: asset.id,
      assetName: asset.name,
      componentIds: getDeploymentComponentIds(deployment, asset.id),
      condition: match.condition,
      ...(match.handoffSessionId ? {
        handoffSessionId: match.handoffSessionId,
        handoffType: match.handoffType || 'return_on_behalf',
        returnedOnBehalfOf: match.returnedOnBehalfOf || deployment?.person || '',
        returnedOnBehalfOfUserId: match.returnedOnBehalfOfUserId || '',
        auditSource: 'gear_handoff_qr',
      } : {}),
    }));
    return { ...asset, status, condition: match.condition, currentDeploymentId: null, updatedAt: timestamp };
  });
  normalizedReturns.forEach(item => { if (!state.assets.some(asset => normalizeAssetId(asset.id) === item.assetId)) throw new Error(`Asset ${item.assetId} was not found.`); });
  const deployments = state.deployments.map(deployment => {
    if (!deploymentIds.has(deployment.id)) return deployment;
    const stillOut = assets.some(asset => asset.currentDeploymentId === deployment.id), closed = !stillOut;
    return { ...deployment, status: closed ? 'closed' : 'returning', loanerRadioReturned: Boolean(loanerReturned[deployment.id]) || deployment.loanerRadioReturned, returnedAt: closed ? timestamp : deployment.returnedAt, updatedAt: timestamp };
  });
  const closeEvents = [...deploymentIds].flatMap(deploymentId => {
    const deployment = deployments.find(item => item.id === deploymentId);
    if (deployment?.status !== 'closed') return [];
    return [event('deployment_closed', returnedBy, { deploymentId, person: deployment.person, incident: deployment.incident, incidentOrderNumber: deployment.incidentOrderNumber || '', incidentIrwinId: deployment.incidentIrwinId || '', incidentSource: deployment.incidentSource || '', deploymentType: deployment.deploymentType || deployment.role || 'EMPF', deploymentSource: deployment.deploymentSource || '', deploymentSourceId: deployment.deploymentSourceId || '', deploymentSourcePosition: deployment.deploymentSourcePosition || '', deploymentSourceLocation: deployment.deploymentSourceLocation || '', componentSelections: deployment.componentSelections || {}, loanerRadio: deployment.loanerRadio, loanerRadioReturned: deployment.loanerRadioReturned })];
  });
  return { ...state, assets, deployments, events: [...returnEvents, ...closeEvents, ...state.events] };
}

export function getDeploymentForAsset(state, asset) {
  if (!asset?.currentDeploymentId) return null;
  return state.deployments.find(item => item.id === asset.currentDeploymentId) || null;
}
